import { RemittanceInvoiceCandidates } from './RemittanceInvoiceCandidates';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ArrowCounterClockwiseIcon,
  Check,
  PencilSimple,
  Plus,
  TrashIcon,
  X,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CustomerLink } from '@/components/accounts-receivable/customer-link';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useConfirmLineItemMatch,
  useRejectLineItem,
  useUndoRejectLineItem,
  useAddRemittanceLineItems,
  useUpdateRemittanceLineItem,
} from '@/api/rpc/hooks/accounts-receivable/useArRemittances';
import { useArInvoice } from '@/api/rpc/hooks/accounts-receivable/useArInvoices';
import { InvoiceSearchCombobox } from './invoice-search-combobox';
import { InvoicePickerCombobox } from '@/components/accounts-receivable/collection-threads/InvoicePickerCombobox';
import { DecimalInput } from '@/components/common/DecimalInput';
import { Currency } from '@/components/common/Currency';
import { useToast } from 'src/context/ToastContext';
import type { RemittanceDetail } from '@erp/rpc';
import {
  canAddLineItem,
  getLineItemActions,
  hasLineItemActions,
  invoiceCustomerLabel,
  isSubmittableAmount,
} from './lineItemRowState';
import { T, useGT } from 'gt-react';

type LineItem = RemittanceDetail['lineItems'][number];

/**
 * The stated amount as it should read to a human: deductions negative.
 *
 * Deduction lines are stored with a POSITIVE `statedAmount` plus
 * `kind: 'deduction'` — the sign lives in the kind, not the number (see
 * `RemittanceLineItemService.addRemittanceLineItems`, which strips the sign a
 * user types and derives the kind from it). Rendering the stored value raw made
 * a withheld amount indistinguishable from a payment, so a remittance paying
 * 17,147.70 less a 2,628.45 deduction read as 19,776.15 collected.
 *
 * Restoring the sign here round-trips the convention the add-line row already
 * advertises ("enter a negative amount for a deduction"), so what you type is
 * what you see. `Currency` formats negatives in accounting style, i.e.
 * ($2,628.45).
 *
 * Prefixed textually rather than negated numerically to leave decimal precision
 * untouched, matching how the service strips it.
 */
function displayStatedAmount(li: LineItem): string | null {
  if (li.statedAmount == null) return null;
  const amount = String(li.statedAmount).trim();
  if (li.kind !== 'deduction' || amount.startsWith('-')) return amount;
  return `-${amount}`;
}

function MatchStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'matched':
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-800 border-green-200"
        >
          <T context="remittance line item match status: invoice confirmed matched">
            Confirmed
          </T>
        </Badge>
      );
    case 'auto_matched':
      return (
        <Badge
          variant="outline"
          className="bg-blue-50 text-blue-800 border-blue-200"
        >
          <T context="remittance line item match status: system auto-matched, awaiting confirmation">
            Auto-matched
          </T>
        </Badge>
      );
    case 'unmatched':
      return (
        <Badge variant="secondary">
          <T context="remittance line item match status: no invoice matched yet">
            Unmatched
          </T>
        </Badge>
      );
    case 'ambiguous':
      return (
        <Badge className="bg-amber-100 text-amber-800 border-amber-200">
          <T context="remittance line item match status: multiple possible invoice matches">
            Ambiguous
          </T>
        </Badge>
      );
    case 'rejected':
      return (
        <Badge variant="outline" className="text-muted-foreground">
          <T context="remittance line item match status: match rejected">
            Rejected
          </T>
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

/**
 * Editable until the line is user-confirmed: once a line is `matched` it may
 * be backed by a payment application, so it's immutable — the correction path
 * is delete + re-add (the backend enforces this too).
 */
function isLineEditable(lineItem: LineItem): boolean {
  return (
    lineItem.matchStatus !== 'matched' && lineItem.matchStatus !== 'rejected'
  );
}

/**
 * Stated amount, inline-editable on editable remittances until the line is
 * confirmed. The edit input is seeded with the signed display value
 * (deductions negative) and saved signed: the service derives `kind` from the
 * sign and stores the absolute value, so flipping the sign converts a payment
 * line to a deduction and back.
 */
function StatedAmountCell({
  lineItem,
  editable,
}: {
  lineItem: LineItem;
  editable: boolean;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const updateLineItem = useUpdateRemittanceLineItem();
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState<string | null>(null);

  const display = displayStatedAmount(lineItem);
  const canSave = isSubmittableAmount(amount) && !updateLineItem.isPending;

  async function handleSave() {
    if (!canSave || amount === null) return;
    try {
      await updateLineItem.mutateAsync({
        lineItemId: lineItem.id,
        statedAmount: amount,
      });
      setEditing(false);
    } catch (err) {
      showToast({
        title: gt('Amount update failed', {
          context: 'toast title, remittance line item amount update failed',
        }),
        message: err instanceof Error ? err.message : '',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  if (!editing) {
    return (
      <div className="group flex items-center gap-1">
        <Currency
          value={display}
          fallback="--"
          className={
            lineItem.kind === 'deduction' ? 'text-destructive' : undefined
          }
        />
        {editable && isLineEditable(lineItem) && (
          <Button
            size="icon"
            variant="ghost"
            className="size-6 text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
            onClick={() => {
              setAmount(display);
              setEditing(true);
            }}
            aria-label={gt('Edit amount', {
              context: 'a11y label, edit remittance line item stated amount',
            })}
          >
            <PencilSimple className="size-3.5" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1"
      onKeyDown={(e) => {
        if (e.key === 'Enter') void handleSave();
        if (e.key === 'Escape') setEditing(false);
      }}
    >
      <DecimalInput
        value={amount}
        onChange={setAmount}
        className="h-8 w-24"
        autoFocus
      />
      <Button
        size="icon"
        variant="ghost"
        className="size-7"
        disabled={!canSave}
        onClick={() => void handleSave()}
        aria-label={gt('Save amount', {
          context: 'a11y label, save remittance line item stated amount',
        })}
      >
        <Check className="size-4" />
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="size-7 text-muted-foreground"
        disabled={updateLineItem.isPending}
        onClick={() => setEditing(false)}
        aria-label={gt('Cancel amount edit', {
          context: 'a11y label, cancel editing remittance line item amount',
        })}
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}

/**
 * Matched invoice, changeable on editable remittances until the line is
 * confirmed. Picking an invoice here is a pre-confirmation edit — the backend
 * moves the line to `auto_matched`, so it still needs an explicit Confirm.
 */
/**
 * The line's resolve mutations, created once per row and shared by the
 * Assign Invoice control and the hover actions, so a pending Assign disables
 * Reject/Undo too and two conflicting requests can't race for the same line.
 */
function useLineItemMutations() {
  const confirmMatch = useConfirmLineItemMatch();
  const rejectLineItem = useRejectLineItem();
  const undoReject = useUndoRejectLineItem();
  return {
    confirmMatch,
    rejectLineItem,
    undoReject,
    pending:
      confirmMatch.isPending ||
      rejectLineItem.isPending ||
      undoReject.isPending,
  };
}

type LineItemMutations = ReturnType<typeof useLineItemMutations>;

/**
 * The Matched Invoice cell: the invoice (or the way to pick one) with the
 * Confirm / Reject decision right beside it, since the invoice match is
 * exactly what the reviewer is confirming or rejecting.
 */
function MatchedInvoiceCell({
  lineItem,
  editable,
  mutations,
}: {
  lineItem: LineItem;
  editable: boolean;
  mutations: LineItemMutations;
}) {
  return (
    <div className="flex items-start gap-2">
      <div className="min-w-0">
        <MatchedInvoiceContent
          lineItem={lineItem}
          editable={editable}
          mutations={mutations}
        />
      </div>
      <MatchDecision lineItem={lineItem} mutations={mutations} />
    </div>
  );
}

function MatchedInvoiceContent({
  lineItem,
  editable,
  mutations,
}: {
  lineItem: LineItem;
  editable: boolean;
  mutations: LineItemMutations;
}) {
  if (lineItem.matchStatus === 'ambiguous') {
    return (
      <div className="space-y-2">
        <RemittanceInvoiceCandidates
          lineItem={lineItem}
          editable={editable && isLineEditable(lineItem)}
        />
        {lineItem.invoiceId == null && (
          <AssignInvoiceControl
            lineItemId={lineItem.id}
            mutations={mutations}
          />
        )}
      </div>
    );
  }
  if (lineItem.matchStatus === 'unmatched' && lineItem.invoiceId == null) {
    return (
      <AssignInvoiceControl lineItemId={lineItem.id} mutations={mutations} />
    );
  }
  return <SuggestedInvoiceCell lineItem={lineItem} editable={editable} />;
}

/**
 * A line with no invoice can't be confirmed, so picking one is the required
 * next step: it stays visible (not hover-revealed) in the Matched Invoice
 * cell. Picking an invoice here confirms the match in one go.
 */
function AssignInvoiceControl({
  lineItemId,
  mutations,
}: {
  lineItemId: string;
  mutations: LineItemMutations;
}) {
  const [searching, setSearching] = useState(false);
  const { confirmMatch, pending } = mutations;

  if (searching) {
    return (
      <InvoiceSearchCombobox
        onSelect={(invoiceId) => {
          confirmMatch.mutate({ lineItemId, invoiceId });
          setSearching(false);
        }}
        onCancel={() => setSearching(false)}
      />
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 px-2 text-xs"
      disabled={pending}
      onClick={() => setSearching(true)}
    >
      <T context="assign invoice to remittance line item">Assign Invoice</T>
    </Button>
  );
}

/**
 * The customer billed on the matched invoice — whose invoice this line pays,
 * which may differ from the remittance's payer.
 */
function InvoiceCustomerCell({ lineItem }: { lineItem: LineItem }) {
  const customerName = lineItem.invoice?.customerName;
  const customerId = lineItem.invoice?.customerId;
  if (!customerName) return <span className="text-muted-foreground">--</span>;
  if (!customerId) return <span>{customerName}</span>;
  return (
    <CustomerLink
      customerId={customerId}
      customerName={customerName}
      erpDisplayName={null}
      className="font-normal text-foreground hover:text-primary"
    />
  );
}

function SuggestedInvoiceCell({
  lineItem,
  editable,
}: {
  lineItem: LineItem;
  editable: boolean;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const updateLineItem = useUpdateRemittanceLineItem();
  const [editing, setEditing] = useState(false);

  function handleSelect(invoiceId: string) {
    setEditing(false);
    updateLineItem.mutate(
      { lineItemId: lineItem.id, invoiceId },
      {
        onError: (err) =>
          showToast({
            title: gt('Invoice update failed', {
              context:
                'toast title, remittance line item invoice update failed',
            }),
            message: err instanceof Error ? err.message : '',
            color: 'error',
            icon: 'warning',
          }),
      },
    );
  }

  if (editing) {
    return (
      <InvoiceSearchCombobox
        onSelect={handleSelect}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="group flex items-center gap-1">
      {lineItem.invoiceId && lineItem.invoice?.invoiceNumber ? (
        <Link
          to="/accounts-receivable/invoices/$arInvoiceId"
          params={{ arInvoiceId: lineItem.invoiceId }}
          viewTransition
          className="text-blue-600 hover:underline"
        >
          {lineItem.invoice.invoiceNumber}
        </Link>
      ) : (
        (lineItem.invoice?.invoiceNumber ?? '--')
      )}
      {editable && isLineEditable(lineItem) && (
        <Button
          size="icon"
          variant="ghost"
          className="size-6 text-muted-foreground opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
          disabled={updateLineItem.isPending}
          onClick={() => setEditing(true)}
          aria-label={gt('Change invoice', {
            context:
              'a11y label, change remittance line item invoice before confirmation',
          })}
        >
          <PencilSimple className="size-3.5" />
        </Button>
      )}
    </div>
  );
}

/**
 * A quiet icon action in the edit pencil's style, with a tooltip naming
 * exactly what it does.
 */
function IconAction({
  label,
  className,
  disabled,
  onClick,
  children,
}: {
  label: string;
  className?: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className={cn('size-6 text-muted-foreground', className)}
          disabled={disabled}
          onClick={onClick}
          aria-label={label}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Confirm / Reject (or Undo) for the line's invoice match, beside the invoice
 * so it reads as a decision about that match. Reviewers rarely confirm line by
 * line, so these are quiet icons like the edit pencil, shown on row hover (and
 * keyboard focus, and always on touch screens) rather than competing with the
 * data.
 */
function MatchDecision({
  lineItem,
  mutations,
}: {
  lineItem: LineItem;
  mutations: LineItemMutations;
}) {
  const gt = useGT();
  const actions = getLineItemActions(lineItem);
  if (!hasLineItemActions(actions)) return null;

  const { confirmMatch, rejectLineItem, undoReject, pending } = mutations;
  const { confirmInvoiceId } = actions;
  const lineItemId = lineItem.id;
  const invoiceNumber = lineItem.invoice?.invoiceNumber ?? '';

  return (
    <div className="flex shrink-0 items-center opacity-0 transition-opacity group-hover/row:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
      {confirmInvoiceId && (
        <IconAction
          label={gt('Confirm match to {invoiceNumber}', {
            invoiceNumber,
            context:
              'tooltip and a11y label, confirm that this remittance line pays the named invoice',
          })}
          className="hover:bg-green-50 hover:text-green-700"
          disabled={pending}
          onClick={() =>
            confirmMatch.mutate({ lineItemId, invoiceId: confirmInvoiceId })
          }
        >
          <Check className="size-3.5" />
        </IconAction>
      )}
      {actions.reject && (
        <IconAction
          label={gt('Reject match', {
            context:
              'tooltip and a11y label, reject the invoice match on this remittance line',
          })}
          className="hover:bg-red-50 hover:text-red-700"
          disabled={pending}
          onClick={() => rejectLineItem.mutate({ lineItemId })}
        >
          <X className="size-3.5" />
        </IconAction>
      )}
      {actions.undo && (
        <IconAction
          label={gt('Undo reject', {
            context:
              'tooltip and a11y label, undo rejecting the invoice match on this remittance line',
          })}
          disabled={pending}
          onClick={() => undoReject.mutate({ lineItemId })}
        >
          <ArrowCounterClockwiseIcon className="size-3.5" />
        </IconAction>
      )}
    </div>
  );
}

/**
 * Delete, revealed on row hover so it stays out of the way: shown on keyboard
 * focus and always on touch screens, which have no hover.
 */
function DeleteLineItemButton({
  lineItemId,
  onDelete,
  deleting,
}: {
  lineItemId: string;
  onDelete: (lineItemId: string) => void;
  deleting: boolean;
}) {
  const gt = useGT();
  return (
    <Button
      size="icon"
      variant="ghost"
      className="ml-auto size-7 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover/row:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100"
      disabled={deleting}
      onClick={() => onDelete(lineItemId)}
      aria-label={gt('Delete line item', {
        context: 'a11y label, delete this remittance line item',
      })}
    >
      <TrashIcon className="size-4" />
    </Button>
  );
}

function RemittanceLineItemRow({
  lineItem,
  editable,
  onDelete,
  deleting,
}: {
  lineItem: LineItem;
  editable: boolean;
  onDelete?: (lineItemId: string) => void;
  deleting: boolean;
}) {
  const mutations = useLineItemMutations();
  return (
    <TableRow className={cn('group/row', rowClassName(lineItem.matchStatus))}>
      <TableCell className="font-mono text-sm">
        {lineItem.rawInvoiceRef}
      </TableCell>
      <TableCell className="text-sm">
        <MatchedInvoiceCell
          lineItem={lineItem}
          editable={editable}
          mutations={mutations}
        />
      </TableCell>
      <TableCell className="max-w-[200px] truncate text-sm">
        <InvoiceCustomerCell lineItem={lineItem} />
      </TableCell>
      <TableCell className="text-sm">
        <StatedAmountCell lineItem={lineItem} editable={editable} />
      </TableCell>
      <TableCell className="text-sm">
        <Currency value={lineItem.invoice?.balanceDue ?? null} fallback="--" />
      </TableCell>
      <TableCell>
        <MatchStatusBadge status={lineItem.matchStatus} />
      </TableCell>
      <TableCell className="text-sm">
        <div className="flex items-center gap-2">
          <LineItemThreadLink lineItem={lineItem} />
          {editable && onDelete && (
            <DeleteLineItemButton
              lineItemId={lineItem.id}
              onDelete={onDelete}
              deleting={deleting}
            />
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Inline "new line" row pinned to the bottom of the table so a user can add
 * lines while reading the remittance email side-by-side. Picking an invoice seeds the
 * amount with its outstanding balance (overridable); a negative amount records
 * a deduction. `kind` and the stored sign are derived server-side.
 *
 * The invoice picker deliberately searches ALL customers' invoices: the
 * customer who sent a remittance sometimes pays other customers' invoices, so
 * restricting to the matched customer hid legitimate targets.
 */
function AddLineItemRow({
  remittanceAdviceId,
  onCancel,
}: {
  remittanceAdviceId: string;
  onCancel: () => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const addLineItems = useAddRemittanceLineItems();

  const [invoiceId, setInvoiceId] = useState<string | null>(null);
  const [amount, setAmount] = useState<string | null>(null);

  const { invoice } = useArInvoice(invoiceId ?? undefined);
  const selectedInvoice = invoice && invoice.id === invoiceId ? invoice : null;

  // The row opens below every existing line, but its trigger sits in the
  // section header: on a long remittance it would open off-screen. Bring it
  // into view once, on mount, and put focus on the invoice picker.
  const rowRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    row.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    row
      .querySelector<HTMLButtonElement>('[role="combobox"]')
      ?.focus({ preventScroll: true });
  }, []);

  // Seed the amount with the invoice's outstanding balance once, when the
  // freshly-picked invoice loads. Keyed on id via a ref so a background refetch
  // can't clobber an amount the user has since edited.
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!invoiceId) {
      seededFor.current = null;
      return;
    }
    if (!selectedInvoice || seededFor.current === invoiceId) return;
    seededFor.current = invoiceId;
    setAmount(selectedInvoice.balanceDue);
  }, [invoiceId, selectedInvoice]);

  const canAdd = canAddLineItem(invoiceId, amount, addLineItems.isPending);

  function handleInvoiceChange(nextInvoiceId: string | null) {
    setInvoiceId(nextInvoiceId);
    // Clear the amount so it re-seeds from the newly-picked invoice's balance.
    setAmount(null);
  }

  async function handleAdd() {
    if (!canAdd || invoiceId === null || amount === null) return;
    try {
      await addLineItems.mutateAsync({
        remittanceAdviceId,
        lineItems: [{ invoiceId, statedAmount: amount }],
      });
      showToast({
        title: gt('Line item added', {
          context: 'toast title, remittance line item added',
        }),
        color: 'success',
        icon: 'check',
      });
      setInvoiceId(null);
      setAmount(null);
      seededFor.current = null;
    } catch (err) {
      showToast({
        title: gt('Add failed', {
          context: 'toast title, remittance line item add failed',
        }),
        message: err instanceof Error ? err.message : '',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  return (
    <TableRow ref={rowRef} className="bg-muted/40">
      <TableCell className="font-mono text-sm text-muted-foreground">
        {selectedInvoice?.invoiceNumber ?? '—'}
      </TableCell>
      <TableCell>
        <InvoicePickerCombobox
          compact
          value={invoiceId}
          onChange={handleInvoiceChange}
          placeholder={gt('Select invoice', {
            context: 'placeholder, remittance line invoice picker',
          })}
        />
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {invoiceCustomerLabel(selectedInvoice)}
      </TableCell>
      <TableCell>
        <DecimalInput
          value={amount}
          onChange={setAmount}
          className="h-7 w-24 text-xs"
          placeholder={gt('Amount', {
            context: 'placeholder, remittance line item amount',
          })}
        />
      </TableCell>
      <TableCell className="text-sm">
        <Currency value={selectedInvoice?.balanceDue ?? null} fallback="--" />
      </TableCell>
      <TableCell>
        <Badge variant="secondary">
          <T context="remittance line item, unsaved new row">New</T>
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={handleAdd}
            disabled={!canAdd}
          >
            <T context="button, add remittance line item">Add</T>
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-7 text-muted-foreground"
            disabled={addLineItems.isPending}
            onClick={onCancel}
            aria-label={gt('Cancel adding line item', {
              context: 'a11y label, cancel the inline add-line row',
            })}
          >
            <X className="size-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function LineItemThreadLink({ lineItem }: { lineItem: LineItem }) {
  if (!lineItem.collectionThreadId || !lineItem.collectionThreadCustomerId) {
    return <span className="text-muted-foreground">--</span>;
  }
  return (
    <Link
      to="/accounts-receivable/collections/$customerId/threads/$threadId"
      params={{
        customerId: lineItem.collectionThreadCustomerId,
        threadId: lineItem.collectionThreadId,
      }}
      viewTransition
      className="font-mono text-blue-600 hover:underline"
    >
      {lineItem.collectionThreadRef ?? (
        <T context="link text, view linked collection thread">View</T>
      )}
    </Link>
  );
}

function rowClassName(status: string): string {
  switch (status) {
    case 'unmatched':
    case 'ambiguous':
      return 'bg-amber-50/50';
    case 'auto_matched':
      return 'bg-blue-50/50';
    case 'rejected':
      return 'opacity-50';
    default:
      return '';
  }
}

interface RemittanceLineItemsTableProps {
  lineItems: LineItem[];
  /** When true (editable remittances), each row's ⋯ menu offers Delete. */
  editable?: boolean;
  /**
   * When set alongside `editable`, a "+ Add" button in the section
   * header reveals an inline add-line row at the bottom of the table.
   */
  remittanceAdviceId?: string;
  /** Section heading, on the left of the header row, followed by Add. */
  heading?: ReactNode;
  /** Secondary header content (e.g. a hint), on the right of the header row. */
  headingAside?: ReactNode;
  onDelete?: (lineItemId: string) => void;
  deletingId?: string | null;
}

export function RemittanceLineItemsTable({
  lineItems,
  editable = false,
  remittanceAdviceId,
  heading,
  headingAside,
  onDelete,
  deletingId,
}: RemittanceLineItemsTableProps) {
  const gt = useGT();
  const [adding, setAdding] = useState(false);
  const canAddLines = editable && remittanceAdviceId != null;

  const addButton = canAddLines && !adding && (
    <Button
      size="sm"
      variant="outline"
      className="h-6 gap-1 px-2 text-xs"
      onClick={() => setAdding(true)}
      aria-label={gt('Add line item', {
        context: 'a11y label, reveal the inline row for a new remittance line',
      })}
    >
      <Plus className="size-3.5" />
      <T context="button, reveal inline add-line row for a new remittance line">
        Add
      </T>
    </Button>
  );

  const header = (heading || addButton || headingAside) && (
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-2">
        {heading}
        {addButton}
      </div>
      {headingAside}
    </div>
  );

  if (lineItems.length === 0 && !adding) {
    return (
      <div className="space-y-2">
        {header}
        <div className="text-sm text-muted-foreground py-4 text-center">
          {canAddLines ? (
            <T context="empty state, remittance has no line items; the Add button is in the section header above">
              No line items yet. Use Add to enter one.
            </T>
          ) : (
            <T context="no remittance line items">No line items</T>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {header}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[140px]">
              <T context="remittance line items table column header">
                Invoice Ref
              </T>
            </TableHead>
            <TableHead className="w-[140px]">
              <T context="remittance line items table column header">
                Matched Invoice
              </T>
            </TableHead>
            <TableHead>
              <T context="remittance line items table column header, customer billed on the matched invoice">
                Customer
              </T>
            </TableHead>
            <TableHead className="w-[100px]">
              <T context="remittance line items table column header, stated amount">
                Stated Amt
              </T>
            </TableHead>
            <TableHead className="w-[100px]">
              <T context="remittance line items table column header">
                Balance Due
              </T>
            </TableHead>
            <TableHead className="w-[100px]">
              <T context="remittance line items table column header">Status</T>
            </TableHead>
            <TableHead>
              <T context="remittance line items table column header, linked collection thread">
                Thread
              </T>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lineItems.map((li) => (
            <RemittanceLineItemRow
              key={li.id}
              lineItem={li}
              editable={editable}
              onDelete={onDelete}
              deleting={deletingId === li.id}
            />
          ))}
          {adding && remittanceAdviceId && (
            <AddLineItemRow
              remittanceAdviceId={remittanceAdviceId}
              onCancel={() => setAdding(false)}
            />
          )}
        </TableBody>
      </Table>
    </div>
  );
}
